import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable, firstValueFrom, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
    AuthStorageMode,
    AuthTokenResponse,
    AuthTokenStorageService,
    StoredAuthToken,
} from './auth-token-storage.service';
import { PageLoaderService } from '../core/page-loader.service';
import { AuthUserService } from './auth-user.service';

@Injectable({
    providedIn: 'root',
})
export class AuthBootstrapService {
    private readonly authBaseUrl = environment.production
        ? 'https://api.cipa.smosc.com.br'
        : 'http://localhost:10051';

    private readonly tokenByCodeEndpoint = `${this.authBaseUrl}/cipa/authentication/token`;
    private readonly tokenRefreshEndpoint = `${this.authBaseUrl}/cipa/authentication/token/refresh`;
    private readonly portariaBaseUrl = environment.production
        ? 'https://portaria.cipa.smosc.com.br'
        : 'http://localhost:10050';

    constructor(
        private readonly loader: PageLoaderService,
        private readonly http: HttpClient,
        private readonly authStorage: AuthTokenStorageService,
        private readonly authUserService: AuthUserService,
    ) { }

    initialize(): Promise<void> {
        return firstValueFrom(this.initialize$());
    }

    private initialize$(): Observable<void> {
        const { code, storageMode } = this.extractAndSanitizeUrlParams();
        const routeContext = this.extractElectionContextFromUrl();

        if (code) {
            return this.requestTokenByCode(code, storageMode).pipe(
                tap((tokenResponse) => this.authStorage.saveToken(this.toAuthTokenResponse(tokenResponse), storageMode)),
                tap(() => {
                    this.loader.hide();
                }),
                map(() => void 0),
                catchError(() => {
                    this.redirectToPortariaFromStoredContext();
                    return of(void 0);
                }),
            );
        }

        const storedToken = this.authStorage.readStoredToken();
        if (!storedToken) {
            this.redirectToPortariaFromStoredContext();
            return of(void 0);
        }

        if (this.hasDifferentStoredElectionContext(routeContext)) {
            this.redirectToPortariaFromStoredContext(routeContext);
            return of(void 0);
        }

        if (!storedToken.refreshToken) {
            this.redirectToPortariaFromStoredContext();
            return of(void 0);
        }

        return this.refreshToken(storedToken).pipe(
            tap((tokenResponse) =>
                this.authStorage.saveToken(this.toAuthTokenResponse(tokenResponse), storedToken.storageMode),
            ),
            tap(() => {
                this.loader.hide();
            }),
            map(() => void 0),
            catchError(() => {
                this.redirectToPortariaFromStoredContext();
                return of(void 0);
            }),
        );
    }

    private redirectToPortariaFromStoredContext(
        routeContext: { companySuffix: string; electionGuid: string } | null = this.extractElectionContextFromUrl(),
    ): void {
        const currentUser = this.authUserService.getCurrentUser();
        const companySuffix = routeContext?.companySuffix ?? currentUser.companySuffix;
        const electionGuid = routeContext?.electionGuid ?? currentUser.electionGuid;
        const path = companySuffix && electionGuid
            ? `/${encodeURIComponent(companySuffix)}/${encodeURIComponent(electionGuid)}`
            : '';

        window.location.assign(`${this.portariaBaseUrl}${path}`);
    }

    private hasDifferentStoredElectionContext(
        routeContext: { companySuffix: string; electionGuid: string } | null,
    ): boolean {
        if (!routeContext) {
            return false;
        }

        const currentUser = this.authUserService.getCurrentUser();
        if (!currentUser.companySuffix || !currentUser.electionGuid) {
            return false;
        }

        return currentUser.companySuffix !== routeContext.companySuffix
            || currentUser.electionGuid !== routeContext.electionGuid;
    }

    private requestTokenByCode(
        code: string,
        storageMode: AuthStorageMode,
    ): Observable<CipaTokenResponse> {
        const endpoint = `${this.tokenByCodeEndpoint}/${encodeURIComponent(code)}`;

        return this.http.get<CipaTokenResponse>(endpoint);
    }

    private refreshToken(storedToken: StoredAuthToken): Observable<CipaTokenResponse> {
        const endpoint = `${this.tokenRefreshEndpoint}/${encodeURIComponent(storedToken.refreshToken ?? '')}`;

        return this.http.post<CipaTokenResponse>(
            endpoint,
            null,
            {
                headers: {
                    ignoreLoadingBar: '',
                },
            },
        );
    }

    private extractAndSanitizeUrlParams(): {
        code: string | null;
        storageMode: AuthStorageMode;
    } {
        const url = new URL(window.location.href);
        const code = url.searchParams.get('code') ?? url.searchParams.get('cd');
        const storageType = url.searchParams.get('s');
        const storageMode: AuthStorageMode = storageType === '1' ? 'local' : 'session';

        if (url.searchParams.has('code') || url.searchParams.has('cd') || url.searchParams.has('s')) {
            url.searchParams.delete('code');
            url.searchParams.delete('cd');
            url.searchParams.delete('s');

            const cleanedUrl = `${url.pathname}${url.search}${url.hash}`;
            window.history.replaceState({}, document.title, cleanedUrl);
        }

        return { code, storageMode };
    }

    private extractElectionContextFromUrl(): { companySuffix: string; electionGuid: string } | null {
        const segments = window.location.pathname
            .split('/')
            .filter((segment) => !!segment)
            .map((segment) => decodeURIComponent(segment.trim()));

        if (segments.length >= 2 && this.isGuid(segments[1])) {
            return {
                companySuffix: segments[0],
                electionGuid: segments[1],
            };
        }

        if (segments.length >= 3 &&
            (segments[0] === 'votacao') &&
            this.isGuid(segments[2])) {
            return {
                companySuffix: segments[1],
                electionGuid: segments[2],
            };
        }

        return null;
    }

    private isGuid(value: string): boolean {
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
    }

    private toAuthTokenResponse(tokenResponse: CipaTokenResponse): AuthTokenResponse {
        return {
            token: tokenResponse.token,
            expiresAt: tokenResponse.expiresAt,
            refreshToken: tokenResponse.refreshToken,
            auxiliaryTime: 0,
        };
    }
}

interface CipaTokenResponse {
    token: string;
    expiresAt: string;
    refreshToken: string;
}

export function initializeAuthenticationAtBootstrap(
    authBootstrapService: AuthBootstrapService,
): () => Promise<void> {
    return () => authBootstrapService.initialize();
}
